import { Slider, type SliderProps } from './slider';
import { SliderLabel } from './slider_label';
import { SliderOutput } from './slider_output';
import { SliderThumb } from './slider_thumb';
import { SliderTrack } from './slider_track';
import { SliderTrackFill } from './slider_track_fill';

export type SliderFieldProps<T> = SliderProps<T> & {
  /**
   * The label for the slider. If not provided, you must provide an `aria-label` or `aria-labelledby` prop to the `Slider` component.
   */
  label?: string;
};

/**
 * A `SliderField` is a composite component that includes a `Slider`, `SliderLabel`, `SliderOutput`, `SliderTrack`, `SliderTrackFill`, and `SliderThumb`.
 *
 * Use this component for simple use cases, and see the `Slider` component for more complex use cases.
 *
 * See https://react-spectrum.adobe.com/react-aria/Slider.html for more details.
 *
 * ```tsx
 * <SliderField label="Opacity" />
 * ```
 */
export function SliderField<T extends number | number[]>(
  props: SliderFieldProps<T>,
) {
  const { label, ...rest } = props;
  return (
    <Slider {...rest}>
      {label ? <SliderLabel>{label}</SliderLabel> : null}
      {label ? <SliderOutput /> : null}
      <SliderTrack>
        <SliderTrackFill />
        <SliderThumb />
      </SliderTrack>
    </Slider>
  );
}
